import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../app/store';
import { CardContent, Stack, Box, Typography } from '@mui/material';
import { Policy as PolicyIcon, Business as BusinessIcon } from '@mui/icons-material';
import { PageContainer, PageCard, SectionHeader, SectionPaper } from '../../common/components/styled-ui-elements';
import GoBackButton from '../../common/components/styled-ui-elements/GoBackButton';
import { PageLoading as Loading } from '../../common/components';
import { 
  PolicyNameEditor,
  PolicyNumberEditor,
  PolicyCategoryEditor,
  PolicyEffectiveDateEditor,
  PolicyReviewDateEditor,
  PolicySummaryEditor,
  PolicyProcedureCodesEditor,
  PolicyInsurancesEditor
} from '../components/fieldEditors';
import { SavePolicyButton, DeletePolicyButton } from '../components/commands';
import { setSelectedCompany, updatePolicy, clearPolicy } from '../../../services/CaseManagementService/policySlice';
import { useGetPolicyQuery } from '../../../services/CaseManagementService';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const PolicyEdit: React.FC = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const company = query.get('company') || '';
  const policyId = query.get('policyId') || '';

  const dispatch = useAppDispatch();
  const policy = useAppSelector((state) => state.policy.currentPolicy);

  // Fetch the policy data
  const { 
    data: policyData, 
    isLoading, 
    error,
    isSuccess,
    refetch
  } = useGetPolicyQuery(policyId, {
    skip: !policyId, // Skip if no policyId is provided
  });

  useEffect(() => {
    // Clear any existing policy data
    dispatch(clearPolicy());
    
    if (company) {
      dispatch(setSelectedCompany(company));
    }
    
    // Refetch the policy data when component mounts
    if (policyId) {
      refetch();
    }
  }, [dispatch, company, policyId, refetch]);

  useEffect(() => {
    // Load the fetched policy data into state
    if (isSuccess && policyData) {
      dispatch(updatePolicy(policyData));
    }
  }, [dispatch, isSuccess, policyData]);

  // Redirect back if missing required params
  useEffect(() => {
    if (!company || !policyId) {
      navigate('/case-management/policies');
    }
  }, [company, policyId, navigate]);

  if (!company || !policyId) {
    return null; // Will redirect
  }

  if (isLoading) {
    return <Loading />;
  }

  if (error) {
    return (
      <PageContainer>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <div>Error loading policy. Please try again.</div>
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Box display="flex" justifyContent="center" px={1}>
        <PageCard sx={{ minWidth: 800, maxWidth: 1300, width: '100%' }}>
          {/* Professional Header */}
          <Box
            sx={{
              backgroundColor: '#ffffff',
              borderBottom: '2px solid #3b82f6',
              p: 2.5,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 600, color: '#1e293b', mb: 0.5 }}>
                Edit Policy
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b' }}>
                Update policy information and settings for {company}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={2}>
              <DeletePolicyButton />
              <SavePolicyButton />
              <GoBackButton />
            </Box>
          </Box>

          <CardContent sx={{ p: 3, backgroundColor: '#ffffff' }}>
            <form autoComplete="off">
              <Stack spacing={2.5}>
                {/* Effective Procedures & Insurance Section - Moved to Top */}
                <Box>
                  <SectionHeader 
                    icon={<BusinessIcon sx={{ mr: 1, color: '#3b82f6', fontSize: 18 }} />} 
                    title="Effective Procedures & Insurance" 
                  />
                  <SectionPaper>
                    <Stack direction="row" spacing={3} sx={{ p: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <PolicyProcedureCodesEditor />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <PolicyInsurancesEditor />
                      </Box>
                    </Stack>
                  </SectionPaper>
                </Box>

                {/* Policy Information Section */}
                <Box>
                  <SectionHeader 
                    icon={<PolicyIcon sx={{ mr: 1, color: '#3b82f6', fontSize: 18 }} />} 
                    title="Policy Information" 
                  />
                  <SectionPaper>
                    <Stack spacing={2.5} sx={{ p: 2 }}>
                      {/* Basic Policy Fields */}
                      <Stack direction="row" spacing={2}>
                        <PolicyNumberEditor />
                        <PolicyNameEditor />
                      </Stack>
                      
                      <Stack direction="row" spacing={2}>
                        <PolicyCategoryEditor />
                        <PolicyEffectiveDateEditor />
                        <PolicyReviewDateEditor />
                      </Stack>

                      {/* Policy Summary */}
                      <PolicySummaryEditor />
                    </Stack>
                  </SectionPaper>
                </Box>
              </Stack>
            </form>
          </CardContent>
        </PageCard>
      </Box>
    </PageContainer>
  );
};

export default PolicyEdit;
